import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft, IconX } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { ClaimStatusChip } from "../components/claim-status-chip";
import { useBuyerClaimDetail } from "../../hooks/use-buyer-warranty-claims";
import type { WarrantyClaimMessageDto, ClaimAttachmentDto } from "../../../../api";

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

const TYPE_LABEL: Record<string, string> = {
  DAMAGE: "Damage",
  DEFECT: "Defect",
  MISSING_PART: "Missing Part",
  DOES_NOT_MATCH_DESCRIPTION: "Does Not Match Description",
  OTHER: "Other",
};

export function BuyerWarrantyClaimDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: claim, isLoading } = useBuyerClaimDetail(id ?? null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  if (isLoading || !claim) {
    return (
      <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
        <ShimmerLoader message="Loading claim details…" lines={8} />
      </div>
    );
  }

  const messages = (claim.messages ?? []) as WarrantyClaimMessageDto[];
  const attachments: ClaimAttachmentDto[] = claim.attachments ?? [];

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(AppRoutes.MY_WARRANTY_CLAIMS)}
          className="flex items-center gap-1 text-textSecondary hover:text-textDark transition-colors"
        >
          <IconArrowLeft size={18} />
          <Text variant="bodySm" tone="body">Back to claims</Text>
        </button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <Heading variant="h2" tone="dark">Warranty Claim</Heading>
          <Text variant="bodySm" tone="muted" className="font-mono mt-0.5">{claim.id}</Text>
        </div>
        <ClaimStatusChip status={claim.status} />
      </div>

      <div className="rounded-2xl border border-borderDefault bg-bgWhite p-6 flex flex-col gap-4 max-md:p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Text variant="bodySmBold" tone="dark">Claim Type</Text>
            <Text variant="body" tone="body">{TYPE_LABEL[claim.claimType] ?? claim.claimType}</Text>
          </div>
          <div>
            <Text variant="bodySmBold" tone="dark">Sale Order</Text>
            <Text variant="body" tone="body">#{claim.saleOrderId}</Text>
          </div>
          <div>
            <Text variant="bodySmBold" tone="dark">Filed</Text>
            <Text variant="body" tone="body">{formatDate(claim.createdAt)}</Text>
          </div>
          <div>
            <Text variant="bodySmBold" tone="dark">Last Updated</Text>
            <Text variant="body" tone="body">{formatDate(claim.updatedAt)}</Text>
          </div>
        </div>
        <div>
          <Text variant="bodySmBold" tone="dark">Description</Text>
          <Text variant="body" tone="body" className="mt-1">{claim.description}</Text>
        </div>
        {attachments.length > 0 && (
          <div>
            <Text variant="bodySmBold" tone="dark">Photos</Text>
            <div className="mt-2 flex flex-wrap gap-2">
              {attachments.map((att) =>
                att.url ? (
                  <button
                    key={att.objectKey}
                    type="button"
                    onClick={() => setLightboxUrl(att.url!)}
                    className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-borderDefault focus:outline-none focus:ring-2 focus:ring-brandPrimary"
                    title={att.fileName}
                  >
                    <img src={att.url} alt={att.fileName} className="h-full w-full object-cover" />
                  </button>
                ) : (
                  <div
                    key={att.objectKey}
                    className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-borderDefault bg-bgLight"
                  >
                    <Text variant="bodyXs" tone="muted" className="text-center px-1">{att.fileName}</Text>
                  </div>
                ),
              )}
            </div>
          </div>
        )}

        {lightboxUrl && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
            onClick={() => setLightboxUrl(null)}
          >
            <button
              type="button"
              onClick={() => setLightboxUrl(null)}
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
              aria-label="Close"
            >
              <IconX size={20} />
            </button>
            <img
              src={lightboxUrl}
              alt="Attachment"
              className="max-h-[90vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-borderDefault bg-bgWhite flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-borderDefault">
          <Heading variant="h3" tone="dark">Message Thread</Heading>
        </div>

        {messages.length === 0 ? (
          <div className="px-6 py-8 flex items-center justify-center">
            <Text variant="body" tone="muted">
              No messages yet. The supplier or admin will respond here.
            </Text>
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-borderDefault">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`px-6 py-4 ${m.authorRole === "seller" || m.authorRole === "admin" ? "bg-accentCyanTint/30" : ""}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Text variant="bodySmBold" tone="dark">
                    {m.authorRole === "admin" ? "Admin" : m.authorRole === "seller" ? "Supplier" : "You"}
                  </Text>
                  <Text variant="bodyXs" tone="muted">{formatDate(m.createdAt)}</Text>
                </div>
                <Text variant="body" tone="body">{m.content}</Text>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default BuyerWarrantyClaimDetailPage;
